import { useEffect, useRef } from 'react';

import { usePendingFlushCoordinator } from '../../contexts/PendingFlushCoordinatorContext';

/**
 * Persists in-progress edits when the tab is hidden or closed, so nothing is
 * lost when the user leaves. A drain with nothing pending is a no-op.
 */
export function useFlushOnClose(): void {
  const coordinator = usePendingFlushCoordinator();

  // Guard against draining twice for a single hide; re-armed when the tab
  // becomes visible again (bfcache restore / tab re-focus).
  const hasFlushedRef = useRef(false);

  useEffect(() => {
    // When the tab is hidden, then persist edits before the page can be frozen.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        hasFlushedRef.current = false;
        return;
      }
      flushOnce();
    };

    // When the page is being unloaded, then persist edits as a final safeguard.
    const onPageHide = () => flushOnce();

    const flushOnce = () => {
      if (hasFlushedRef.current) return;
      hasFlushedRef.current = true;
      coordinator.flushImmediately();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [coordinator]);
}
